import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, Download, Grid3x3, ListChecks, MessageSquareQuote, TrendingUp, BarChart3 } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { useSearchParams } from 'react-router-dom'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import ReportsTabs from '../components/ReportsTabs'
import Select from '../components/Select'
import MultiSelect from '../components/MultiSelect'
import LoadingSpinner from '../components/LoadingSpinner'
import InfoTooltip from '../components/InfoTooltip'
import { ChartCard, Badge } from '../components/ChartCard'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart'
import { GRID_STROKE, AXIS_TICK } from '../lib/chartOpts'
import { exportSurveyResponsesCsv } from '../lib/exportCsv'
import {
  answerSplit,
  askedGroups,
  byTeams,
  choiceShares,
  distribution,
  forAudience,
  formatNps,
  groups,
  hasBoard,
  nps,
  partnerName,
  placeName,
  positive,
  positiveMeaning,
  positiveOverTime,
  positiveOverall,
  responseRate,
  scores,
  seriesKey,
  shortLabel,
  slotOf,
  weakest,
  type Audience,
  type Group,
  type Nps,
  type Share,
} from '../lib/surveyResults'
import type { SurveyAnswer, SurveyQuestion, SurveyRoundResults, SurveySubmission, SurveyTypeResults, SurveyTypeSummary } from '../types'

const RAG = { green: '#00843D', amber: '#F88806', red: '#EF3340' } as const
const TINT = {
  green: 'bg-[#00843D]/10 text-[#00843D]',
  amber: 'bg-[#F88806]/10 text-[#F88806]',
  red: 'bg-[#EF3340]/10 text-[#EF3340]',
} as const
type Tone = keyof typeof RAG

// Positive: green from three in four, red below half. NPS: green from +30, red below 0.
const positiveTone = (pct: number): Tone => (pct >= 75 ? 'green' : pct >= 50 ? 'amber' : 'red')
const npsTone = (score: number): Tone => (score >= 30 ? 'green' : score >= 0 ? 'amber' : 'red')

// Lowest answer first: two negative, two positive.
const STAR_COLOURS = ['#EF3340', '#F8A04B', '#7CC49A', '#00843D']
const CHOICE_COLOURS = ['#005EB8', '#00ACED', '#94A3B8', '#CBD5E1', '#002677']
const LINE_COLOURS = ['#005EB8', '#00ACED', '#F88806', '#00843D', '#7C3AED', '#E11D48', '#64748B']

// Fewer answers than this and a figure is shown faded, as one or two people.
const FEW = 3

const fmtDay = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

const of = (round: SurveyRoundResults, ids: string[]) => round.submissions.filter(s => ids.includes(slotOf(s)))

const AUDIENCES: readonly (readonly [Audience, string])[] = [['everyone', 'Everyone'], ['board', 'Board only'], ['others', 'Without Board']]

function BoardTag() {
  return <span className="inline-flex px-1.5 py-px rounded bg-[#002677]/10 text-[#002677] text-[10px] font-bold uppercase tracking-wide">Board</span>
}

const percent = (n: number) => `${n}%`
const score = (n: number) => (n > 0 ? `+${n}` : `${n}`)

/** The change since the last round in words, naming the figure it was, so
 *  there are no points or arrows to decode: "Up from 72% in H1 2026". */
function Change({ now, before, round, format, className = 'text-xs' }: {
  now: number | null; before: number | null | undefined; round?: string; format: (n: number) => string; className?: string
}) {
  if (now === null || before === null || before === undefined || !round) return null
  const tone = now > before ? 'text-[#00843D]' : now < before ? 'text-red-700' : 'text-slate-500'
  const text = now > before ? `Up from ${format(before)} in ${round}` : now < before ? `Down from ${format(before)} in ${round}` : `Same as ${round}`
  return <div className={`font-medium ${tone} ${className}`}>{text}</div>
}

export default function SurveyResults() {
  useDocumentTitle('Surveys')
  const api = useApi()
  const { user, loading: authLoading } = useAuth()
  const allowed = !!user?.canReadSurveyResults

  const { data: types, loading: typesLoading } = useData<SurveyTypeSummary[]>(
    () => (allowed ? api.getSurveyResultTypes() : Promise.resolve([])),
    [allowed],
  )
  // ?survey=<id>&round=<id> pick what is shown, so a link can open it directly.
  const [params, setParams] = useSearchParams()
  const pickedType = params.get('survey') ?? ''
  // Only surveys with a sent round have anything to show, so only those are
  // offered; a link can still open one that hasn't been sent.
  const listed = types?.filter(t => t.rounds > 0) ?? []
  const typeId = pickedType || listed[0]?.id || types?.[0]?.id || ''
  const { data: loaded, loading: resultsLoading } = useData<SurveyTypeResults | null>(
    () => (typeId ? api.getSurveyResults(typeId) : Promise.resolve(null)),
    [typeId],
  )
  // Everyone, or the Board read on its own, or everyone but the Board: every
  // figure below follows, round on round.
  const [audience, setAudience] = useState<Audience>('everyone')
  const board = !!loaded && hasBoard(loaded)
  const who: Audience = board ? audience : 'everyone'
  const results = useMemo(
    () => (loaded ? { ...loaded, rounds: loaded.rounds.map(r => forAudience(r, who)) } : loaded),
    [loaded, who],
  )

  const pickedRound = params.get('round') ?? ''
  const setPickedRound = (id: string) => setParams({ survey: typeId, round: id }, { replace: true })
  const [picked, setPicked] = useState<string[]>([])
  const rounds = results?.rounds ?? []
  const index = Math.max(0, rounds.findIndex(r => r.id === pickedRound))
  const roundIndex = pickedRound && rounds.some(r => r.id === pickedRound) ? index : rounds.length - 1
  const round = rounds[roundIndex]
  const previous = roundIndex > 0 ? rounds[roundIndex - 1] : undefined

  const all = useMemo(() => (round ? askedGroups(round) : []), [round])
  const everyone = loaded?.rounds.find(r => r.id === round?.id)
  const shown = picked.length ? all.filter(g => picked.includes(g.name)) : all
  const ids = shown.flatMap(g => g.departmentIds)

  const pickType = (id: string) => { setParams({ survey: id }, { replace: true }); setPicked([]); setAudience('everyone') }
  const toggleGroup = (name: string) => setPicked(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]))

  if (authLoading || typesLoading || (typeId && resultsLoading)) return <LoadingSpinner />

  if (!allowed) return <Empty text="You don't have access to survey results." />
  if (!results || !types?.length) return <Empty text="No surveys yet." />

  const teams = round ? byTeams(round) : false

  return (
    <div>
      <ReportsTabs active="surveys" />

      {listed.length > 1 && (
        <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold bg-white w-fit mb-4">
          {listed.map(t => (
            <button key={t.id} type="button" onClick={() => pickType(t.id)}
              className={`px-3 py-1.5 ${t.id === typeId ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {t.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-2xl font-bold text-slate-800 truncate">{results.name}</h1>
          {round && (
            <>
              <Badge cls={round.status === 'open' ? 'bg-[#00ACED]/15 text-[#0077A8]' : 'bg-slate-100 text-slate-600'}>
                {round.status === 'open' ? 'Open' : 'Closed'}
              </Badge>
              <span className="text-xs text-slate-400 whitespace-nowrap">Sent {fmtDay(round.sentAt)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {board && everyone && (
            <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold bg-white" role="group" aria-label="Whose answers">
              {AUDIENCES.map(([value, label]) => {
                const n = forAudience(everyone, value).submissions.length
                return (
                  <button key={value} type="button" onClick={() => { setAudience(value); setPicked([]) }}
                    className={`px-3 py-2 flex items-center gap-1.5 ${who === value ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                    {label}
                    <span className={`tabular-nums ${who === value ? 'text-white/70' : 'text-slate-400'}`}>{n}</span>
                  </button>
                )
              })}
            </div>
          )}
          {rounds.length > 0 && (
            <Select value={round?.id ?? ''} onChange={id => setPickedRound(id)} options={[...rounds].reverse().map(r => [r.id, r.name] as const)} />
          )}
          {all.length > 1 && (
            <MultiSelect values={picked} onChange={setPicked} options={all.map(g => g.name)}
              placeholder={teams ? 'All partner teams' : 'All departments'} />
          )}
          {round && (
            <button onClick={() => exportSurveyResponsesCsv(results.name, round, of(round, ids))}
              className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm">
              <Download className="w-4 h-4" /> Export
            </button>
          )}
        </div>
      </div>

      {!round ? (
        <div className="card p-8 text-center text-sm text-slate-500">No rounds sent yet.</div>
      ) : (
        <>
          <Headline round={round} previous={previous} groupsShown={shown} />

          <SplitKey split={answerSplit(round)} />
          {/* One row when it fits (up to five), otherwise rows of three. */}
          <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: `repeat(${all.length <= 5 ? Math.max(all.length, 1) : 3}, minmax(0, 1fr))` }}>
            {all.map(g => (
              <Scorecard key={g.key} round={round} previous={previous} group={g}
                selected={picked.includes(g.name)} faded={picked.length > 0 && !picked.includes(g.name)}
                showDepartments={teams} onClick={() => toggleGroup(g.name)} />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <Questions round={round} submissions={of(round, ids)} />
            <OverTime results={results} series={shown.map(g => ({ key: seriesKey(g), name: g.name }))} current={round.name} grouped={teams} />
          </div>

          <div className="mb-4">
            <Comments round={round} groupsShown={shown} tagBoard={who === 'everyone'} />
          </div>

          {teams && (
            <div className="mb-4">
              <Departments round={round} groupsShown={shown} />
            </div>
          )}

          <div className="mb-8">
            <Responses round={round} submissions={of(round, ids)} showBoard={board} />
          </div>
        </>
      )}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return (
    <div>
      <ReportsTabs active="surveys" />
      <div className="card p-8 text-center text-sm text-slate-500">{text}</div>
    </div>
  )
}

function npsFor(round: SurveyRoundResults | undefined, ids: string[]): Nps | null {
  const q = round?.questions.find(x => x.kind === 'scale')
  return round && q ? nps(scores(of(round, ids), q.id)) : null
}

function Headline({ round, previous, groupsShown }: { round: SurveyRoundResults; previous?: SurveyRoundResults; groupsShown: Group[] }) {
  const ids = groupsShown.flatMap(g => g.departmentIds)
  const names = groupsShown.map(g => g.name)
  const prevIds = previous ? groups(previous).filter(g => names.includes(g.name)).flatMap(g => g.departmentIds) : []
  const now = positiveOverall(round, of(round, ids))
  const before = previous ? positiveOverall(previous, of(previous, prevIds)).pct : undefined
  const npsNow = npsFor(round, ids)
  const npsBefore = previous ? npsFor(previous, prevIds)?.score : undefined
  const rate = responseRate(round, ids)
  const focus = weakest({ ...round, departments: round.departments.filter(d => ids.includes(d.id)) })

  const Stat = ({ label, info, children }: { label: string; info: ReactNode; children: ReactNode }) => (
    <div className="px-6 first:pl-0 last:pr-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        {label}
        <InfoTooltip>{info}</InfoTooltip>
      </div>
      {children}
    </div>
  )

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
      <div className="grid grid-cols-4 divide-x divide-slate-200">
        <Stat label="Positive" info={<><strong>Positive</strong><p>Share of all rating answers that were {positiveMeaning(round)}. Not-applicable answers are left out.</p></>}>
          <div className="font-danone text-4xl font-extrabold leading-tight text-slate-800 mt-1">{now.pct === null ? '-' : `${now.pct}%`}</div>
          <div className="text-sm text-slate-500 mt-1 first-letter:uppercase">{positiveMeaning(round)}, from {now.n} ratings</div>
          <Change now={now.pct} before={before} round={previous?.name} format={percent} className="text-sm mt-1" />
        </Stat>
        <Stat label="Recommend" info={<><strong>NPS</strong><p>% scoring 9-10 minus % scoring 0-6, from -100 to +100.</p></>}>
          <div className="font-danone text-4xl font-extrabold leading-tight text-slate-800 mt-1">{formatNps(npsNow)}</div>
          <div className="text-sm text-slate-500 mt-1">NPS from {npsNow?.n ?? 0} answers</div>
          <Change now={npsNow?.score ?? null} before={npsBefore} round={previous?.name} format={score} className="text-sm mt-1" />
        </Stat>
        <Stat label="Responses" info={<><strong>Responses</strong><p>Answers received ÷ people sent their link.</p></>}>
          <div className="font-danone text-4xl font-extrabold leading-tight text-slate-800 mt-1">{rate.pct === null ? '-' : `${rate.pct}%`}</div>
          <div className="text-sm text-slate-500 mt-1">{rate.responded} of {rate.invited} answered, {round.status === 'open' ? 'round open' : `closed ${fmtDay(round.closedAt)}`}</div>
        </Stat>
        <Stat label="Lowest score" info={<><strong>Lowest score</strong><p>The rating with the lowest positive share for any {byTeams(round) ? 'partner team' : 'department'}, among those with at least {FEW} answers.</p></>}>
          {focus ? (
            <>
              <div className="text-xl font-bold leading-tight text-slate-800 mt-2 truncate">{focus.group.name}</div>
              <div className="text-sm text-slate-500 mt-1">{shortLabel(focus.question, 40)}, {focus.positive.pct}% positive</div>
            </>
          ) : <div className="text-sm text-slate-500 mt-3">Not enough answers yet</div>}
        </Stat>
      </div>
    </div>
  )
}

function Bar({ pct }: { pct: number | null }) {
  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      {pct !== null && <div className="h-full rounded-full bg-[#005EB8]" style={{ width: `${pct}%` }} />}
    </div>
  )
}

type Split = ReturnType<typeof answerSplit>

/** Positive answers green, the best darkest, so the green always matches the
 *  positive figure; the rest amber, the worst red. On four answers this is
 *  how the question charts colour them. */
const splitColour = (split: Split, j: number) =>
  STAR_COLOURS[split[j].positive ? (j === 0 ? 3 : 2) : (j === split.length - 1 ? 0 : 1)]

/** A team's answers as one bar, best answer first, so the green from the left
 *  is its positive share. Each share's figure is on hover, and listed while
 *  the card has keyboard focus, since a keyboard can't hover; the key is shown
 *  once above the cards, since every team in a round has the same answers. */
function AnswerSplit({ split }: { split: Split }) {
  const total = split.reduce((n, s) => n + s.count, 0)
  const figure = (s: Split[number]) => `${s.label}: ${s.count} (${Math.round((s.count / total) * 100)}%)`
  return (
    <div className="relative">
      <div className="flex gap-px h-2.5 rounded-full overflow-hidden bg-slate-100" aria-hidden>
        {total > 0 && split.map((s, j) => s.count > 0 && (
          <div key={s.label} title={figure(s)}
            style={{ width: `${(s.count / total) * 100}%`, background: splitColour(split, j) }} />
        ))}
      </div>
      {total > 0 && (
        <>
          <span className="sr-only">{split.map(figure).join(', ')}</span>
          <span aria-hidden className="hidden group-focus-visible:block absolute inset-x-0 top-full mt-2 z-10 rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-xl">
            {split.map(s => <span key={s.label} className="block tabular-nums">{figure(s)}</span>)}
          </span>
        </>
      )}
    </div>
  )
}

function SplitKey({ split }: { split: Split }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-[11px] text-slate-500">
      {split.map((s, j) => (
        <span key={s.label} className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: splitColour(split, j) }} />
          {s.label}
        </span>
      ))}
    </div>
  )
}

function Scorecard({ round, previous, group, selected, faded, showDepartments, onClick }: {
  round: SurveyRoundResults; previous?: SurveyRoundResults; group: Group
  selected: boolean; faded: boolean; showDepartments: boolean; onClick: () => void
}) {
  const subs = of(round, group.departmentIds)
  const prevGroup = previous ? groups(previous).find(g => g.name === group.name) : undefined
  const prevSubs = previous && prevGroup ? of(previous, prevGroup.departmentIds) : []
  const overall = positiveOverall(round, subs)
  const before = previous && prevGroup ? positiveOverall(previous, prevSubs).pct : undefined
  const rate = responseRate(round, group.departmentIds)
  const ratings = round.questions.filter(q => q.kind === 'rating')
  const recommend = round.questions.find(q => q.kind === 'scale')
  const npsNow = recommend ? nps(scores(subs, recommend.id)) : null
  const npsBefore = recommend && previous && prevGroup ? nps(scores(prevSubs, recommend.id)).score : undefined
  const few = overall.n > 0 && subs.length < FEW

  return (
    <button type="button" onClick={onClick}
      className={`group text-left bg-white rounded-2xl border shadow-sm hover:border-slate-300 p-5 flex flex-col ${selected ? 'border-[#00ACED] ring-2 ring-[#00ACED]/20' : 'border-slate-200'} ${faded ? 'opacity-50' : ''}`}>
      {/* The team only: feedback is on teams, never on the people leading them. */}
      <div className="font-bold text-slate-800 leading-snug truncate" title={group.name}>{group.name}</div>

      <div className="flex items-baseline gap-1.5 mt-4">
        <span className="text-[32px] font-extrabold leading-none tracking-tight tabular-nums text-slate-800">
          {overall.pct === null ? '-' : `${overall.pct}%`}
        </span>
        <span className="text-sm font-medium text-slate-500">positive</span>
      </div>
      <Change now={overall.pct} before={before} round={previous?.name} format={percent} className="text-xs mt-1.5" />
      <div className="mt-3">
        <AnswerSplit split={answerSplit(round, subs)} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-3 text-[11px] text-slate-400">
        <span className="tabular-nums"><span className="font-semibold text-slate-600">{rate.responded} of {rate.invited}</span> answered</span>
        {few && <span className="font-medium text-[#B45309]" title={`Fewer than ${FEW} answers`}>Small sample</span>}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 space-y-2.5 flex-1">
        {ratings.map(q => {
          const p = positive(subs, q)
          return (
            <div key={q.id}>
              <div className="flex items-center justify-between gap-2 text-[11px] mb-1">
                <span className="text-slate-500 truncate">{shortLabel(q, 28)}</span>
                <span className="tabular-nums font-semibold text-slate-700">{p.pct === null ? '-' : `${p.pct}%`}</span>
              </div>
              <Bar pct={p.pct} />
            </div>
          )
        })}
        {npsNow && (
          <div className="pt-1">
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-slate-500">{shortLabel(recommend!, 28)} <span className="text-slate-400">(NPS)</span></span>
              <span className="text-sm font-bold tabular-nums text-slate-800">{formatNps(npsNow)}</span>
            </div>
            <Change now={npsNow.score} before={npsBefore} round={previous?.name} format={score} className="text-[11px] mt-0.5" />
          </div>
        )}
      </div>

      {showDepartments && (
        <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] leading-relaxed text-slate-400 min-h-[2.25rem]">
          {group.partnered ? group.departmentIds.map(id => placeName(round, id)).join(', ') : `No partner team, asked about ${round.partnerFallback || 'IT'}`}
        </div>
      )}
    </button>
  )
}

/** Negative answers run left of the line, positive right of it. */
function Diverging({ submissions, question }: { submissions: SurveySubmission[]; question: SurveyQuestion }) {
  const { counts, na } = distribution(submissions, question)
  const total = counts.reduce((a, b) => a + b, 0)
  const half = Math.floor(counts.length / 2)
  const pct = (n: number) => (total ? (n / total) * 100 : 0)
  // Shares wide enough to hold their number show it.
  const segment = (i: number) => (
    <div key={i} title={`${question.labels[i]}: ${counts[i]} (${Math.round(pct(counts[i]))}%)`}
      className="h-full flex items-center justify-center text-[10px] font-bold text-white tabular-nums overflow-hidden"
      style={{ width: `${pct(counts[i])}%`, background: STAR_COLOURS[Math.round((i / (counts.length - 1)) * 3)] }}>
      {pct(counts[i]) >= 12 && `${Math.round(pct(counts[i]))}%`}
    </div>
  )
  const p = positive(submissions, question)
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold text-slate-700">{shortLabel(question, 40)}</span>
        <span className="text-slate-500">
          <span className="font-bold text-slate-800 tabular-nums">{p.pct === null ? '-' : `${p.pct}%`}</span> positive
          {na > 0 && <span className="text-slate-400">, {na} {question.naLabel.toLowerCase() || 'n/a'}</span>}
        </span>
      </div>
      <div className="flex h-6">
        <div className="w-1/2 flex justify-end bg-slate-50 rounded-l-md overflow-hidden">
          {counts.slice(0, half).map((_, i) => segment(i))}
        </div>
        <div className="w-0.5 -my-1 bg-slate-500 rounded-full" />
        <div className="w-1/2 flex bg-slate-50 rounded-r-md overflow-hidden">
          {counts.slice(half).map((_, i) => segment(i + half))}
        </div>
      </div>
    </div>
  )
}

function NpsBar({ submissions, question }: { submissions: SurveySubmission[]; question: SurveyQuestion }) {
  const n = nps(scores(submissions, question.id))
  const w = (count: number) => (n.n ? (count / n.n) * 100 : 0)
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold text-slate-700">{shortLabel(question, 40)}</span>
        <span className="text-slate-500"><span className="font-bold text-slate-800 tabular-nums">{formatNps(n)}</span> NPS</span>
      </div>
      <div className="flex h-6 rounded-md overflow-hidden bg-slate-50 text-[10px] font-bold tabular-nums">
        <div title={`Detractors (0-6): ${n.detractors}`} className="flex items-center justify-center text-white" style={{ width: `${w(n.detractors)}%`, background: RAG.red }}>{w(n.detractors) >= 6 && n.detractors}</div>
        <div title={`Passives (7-8): ${n.passives}`} className="flex items-center justify-center text-slate-600" style={{ width: `${w(n.passives)}%`, background: '#CBD5E1' }}>{w(n.passives) >= 6 && n.passives}</div>
        <div title={`Promoters (9-10): ${n.promoters}`} className="flex items-center justify-center text-white" style={{ width: `${w(n.promoters)}%`, background: RAG.green }}>{w(n.promoters) >= 6 && n.promoters}</div>
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 tabular-nums">
        <span>Detractors, 0-6</span><span>Passives, 7-8</span><span>Promoters, 9-10</span>
      </div>
    </div>
  )
}

function ChoiceBar({ submissions, question }: { submissions: SurveySubmission[]; question: SurveyQuestion }) {
  const shares = choiceShares(submissions, question)
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">{shortLabel(question, 40)}</div>
      <div className="flex h-6 rounded-md overflow-hidden bg-slate-50">
        {shares.map((s, i) => (
          <div key={s.option} title={`${s.option}: ${s.share.pct ?? 0}%`} style={{ width: `${s.share.pct ?? 0}%`, background: CHOICE_COLOURS[i % CHOICE_COLOURS.length] }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 text-[10px] text-slate-500 mt-1">
        {shares.map((s, i) => (
          <span key={s.option} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: CHOICE_COLOURS[i % CHOICE_COLOURS.length] }} />
            {s.option} <span className="font-semibold text-slate-700 tabular-nums">{s.share.pct ?? 0}%</span>
          </span>
        ))}
      </div>
    </div>
  )
}

function Questions({ round, submissions }: { round: SurveyRoundResults; submissions: SurveySubmission[] }) {
  const asked = round.questions.filter(q => q.kind !== 'text')
  const labels = round.questions.find(q => q.kind === 'rating')?.labels ?? []
  return (
    <ChartCard icon={BarChart3} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Question by Question"
      subtitle="How each question was answered"
      footer={labels.length > 0 ? (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {labels.map((label, i) => (
            <span key={label} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-3 h-3 rounded-sm" style={{ background: STAR_COLOURS[Math.round((i / (labels.length - 1)) * 3)] }} />{label}
            </span>
          ))}
        </div>
      ) : undefined}>
      {labels.length > 0 && (
        <div className="flex text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
          <span className="w-1/2 text-right pr-3">Negative</span>
          <span className="w-1/2 pl-3">Positive</span>
        </div>
      )}
      <div className="space-y-5">
        {asked.map(q => (
          q.kind === 'rating' ? <Diverging key={q.id} submissions={submissions} question={q} />
            : q.kind === 'scale' ? <NpsBar key={q.id} submissions={submissions} question={q} />
              : <ChoiceBar key={q.id} submissions={submissions} question={q} />
        ))}
      </div>
    </ChartCard>
  )
}

function OverTime({ results, series, current, grouped }: {
  results: SurveyTypeResults
  series: { key: string; name: string }[]
  current: string
  grouped: boolean
}) {
  const { rows } = positiveOverTime(results)
  const lines = ['Overall', ...series.map(s => s.name)]
  const config: ChartConfig = Object.fromEntries(lines.map((name, i) => [
    `l${i}`, { label: name, color: i === 0 ? '#002677' : LINE_COLOURS[(i - 1) % LINE_COLOURS.length] },
  ]))
  // Series are keyed l0 (overall), l1... by position, never by a group's name.
  const data = rows.map(row => ({
    round: row.round,
    l0: row.overall,
    ...Object.fromEntries(series.map((s, i) => [`l${i + 1}`, row.groups.get(s.key) ?? null])),
  }))
  return (
    <ChartCard icon={TrendingUp} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="Over Time"
      subtitle={`Positive per round, overall and by ${grouped ? 'partner team' : 'department'}`}
      footer={results.rounds.length > 1 ? (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {lines.map((name, i) => (
            <span key={`l${i}`} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-3 h-0.5 rounded" style={{ background: config[`l${i}`].color, height: i === 0 ? 3 : 2 }} />{name}
            </span>
          ))}
        </div>
      ) : undefined}>
      {results.rounds.length < 2 ? (
        <div className="h-80 flex flex-col items-center justify-center text-center">
          <div className="text-sm font-semibold text-slate-600">{current} is the first round</div>
          <div className="text-xs text-slate-400 mt-1">The next round will show here against it.</div>
        </div>
      ) : (
        <ChartContainer config={config} className="aspect-auto h-80 w-full">
          <LineChart data={data} margin={{ top: 8, right: 32, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
            <XAxis dataKey="round" tickLine={false} axisLine={false} tick={AXIS_TICK} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={v => `${v}%`} />
            <ChartTooltip cursor={{ stroke: GRID_STROKE }} content={<ChartTooltipContent />} />
            {lines.map((name, i) => (
              <Line key={`l${i}`} type="monotone" dataKey={`l${i}`} name={name} stroke={`var(--color-l${i})`}
                strokeWidth={i === 0 ? 3 : 1.75} strokeOpacity={i === 0 ? 1 : 0.8} connectNulls
                dot={{ fill: `var(--color-l${i})`, r: i === 0 ? 4 : 3, strokeWidth: 1.5, stroke: '#fff' }} />
            ))}
          </LineChart>
        </ChartContainer>
      )}
    </ChartCard>
  )
}

function Comments({ round, groupsShown, tagBoard }: { round: SurveyRoundResults; groupsShown: Group[]; tagBoard: boolean }) {
  const written = round.questions.filter(q => q.kind === 'text')
  const byGroup = groupsShown.map(g => ({
    group: g,
    quotes: of(round, g.departmentIds).flatMap(s => written
      .map(q => ({ q, text: s.answers[q.id], s }))
      .filter((x): x is { q: SurveyQuestion; text: string; s: SurveySubmission } => typeof x.text === 'string' && x.text.trim() !== '')),
  })).filter(x => x.quotes.length > 0)
  const total = byGroup.reduce((n, x) => n + x.quotes.length, 0)
  return (
    <ChartCard icon={MessageSquareQuote} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="What People Said"
      subtitle={written.map(q => q.title || q.text).join(', ')}
      badge={<Badge cls="bg-slate-100 text-slate-600">{total} comments</Badge>}>
      {total === 0 ? <p className="text-sm text-slate-400 text-center py-4">No comments yet</p> : (
        <div className="columns-2 gap-6">
          {byGroup.map(({ group, quotes }) => (
            <div key={group.key} className="break-inside-avoid mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{group.name}</span>
                <span className="text-[11px] text-slate-400">{quotes.length}</span>
              </div>
              <div className="space-y-2">
                {quotes.map(({ text, s, q }) => (
                  <div key={`${s.id}-${q.id}`} className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                    <div className="text-sm text-slate-700 leading-snug">{text}</div>
                    <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5">
                      {tagBoard && s.board && <BoardTag />}
                      {placeName(round, slotOf(s))}, {fmtDay(s.submittedOn)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  )
}

function Cell({ share, n }: { share: Share | null; n?: number }) {
  if (!share || share.pct === null) return <td className="px-2 py-1.5 text-center text-slate-300">-</td>
  const few = (n ?? share.n) < FEW
  return (
    <td className="px-1 py-1">
      <div className={`rounded-md text-center py-1 text-[11px] font-semibold tabular-nums ${TINT[positiveTone(share.pct)]} ${few ? 'opacity-50' : ''}`}
        title={`${share.count} of ${share.n} positive`}>
        {share.pct}%
      </div>
    </td>
  )
}

function Departments({ round, groupsShown }: { round: SurveyRoundResults; groupsShown: Group[] }) {
  const ratings = round.questions.filter(q => q.kind === 'rating')
  const recommend = round.questions.find(q => q.kind === 'scale')
  const row = (ids: string[], name: string, strong: boolean, key: string) => {
    const subs = of(round, ids)
    const rate = responseRate(round, ids)
    const n = recommend ? nps(scores(subs, recommend.id)) : null
    return (
      <tr key={key} className={strong ? 'bg-slate-50/60' : ''}>
        <td className={`px-3 py-1.5 whitespace-nowrap ${strong ? 'font-bold text-slate-800' : 'pl-7 text-slate-600'}`}>{name}</td>
        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
          {rate.responded}/{rate.invited}
        </td>
        <Cell share={positiveOverall(round, subs)} n={subs.length} />
        {ratings.map(q => <Cell key={q.id} share={positive(subs, q)} />)}
        {recommend && (
          <td className="px-2 py-1.5 text-center">
            {!n || n.score === null ? <span className="text-slate-300">-</span>
              : <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums ${TINT[npsTone(n.score)]} ${n.n < FEW ? 'opacity-50' : ''}`}>{formatNps(n)}</span>}
          </td>
        )}
      </tr>
    )
  }
  return (
    <ChartCard icon={Grid3x3} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title="By Department"
      subtitle={`Positive answers by question. Pale cells have fewer than ${FEW} answers.`}
      info={<><strong>By department</strong><p>Each answer counts against the department its person was invited from, under the partner team it was asked about. A department split into areas, such as CCD, is counted under the team of the area each person chose. A department with no partner team stands on its own; its questions named {round.partnerFallback || 'IT'}. Board members count in their own department; use Board only to read them on their own.</p></>}>
      <div className="overflow-hidden rounded-lg border border-slate-100">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <th className="text-left font-semibold px-3 py-2">Partner team / department</th>
              <th className="text-right font-semibold px-3 py-2">Answered</th>
              <th className="text-center font-semibold px-2 py-2">All ratings</th>
              {ratings.map(q => <th key={q.id} className="text-center font-semibold px-2 py-2">{shortLabel(q, 24)}</th>)}
              {recommend && <th className="text-center font-semibold px-2 py-2">{shortLabel(recommend, 24)}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groupsShown.map(g => (
              <Fragment key={g.key}>
                {row(g.departmentIds, g.name, true, g.key)}
                {g.partnered && g.departmentIds
                  .filter(id => responseRate(round, [id]).invited > 0 || of(round, [id]).length > 0)
                  .map(id => row([id], placeName(round, id), false, id))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  )
}

function answerText(q: SurveyQuestion, value: SurveyAnswer | undefined) {
  if (value === null || value === undefined || value === '') return <span className="text-slate-300">-</span>
  if (value === 'na' && q.kind !== 'text') return <span className="text-slate-400">{q.naLabel || 'N/A'}</span>
  return value
}

function Responses({ round, submissions, showBoard }: { round: SurveyRoundResults; submissions: SurveySubmission[]; showBoard: boolean }) {
  const [open, setOpen] = useState(false)
  const sorted = [...submissions].sort((a, b) => b.submittedOn.localeCompare(a.submittedOn))
  const asked = round.questions.filter(q => q.kind !== 'text')
  const written = round.questions.filter(q => q.kind === 'text')
  const teams = byTeams(round)
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-6 py-4">
        <span className="flex items-center gap-3">
          <span className="w-10 h-10 bg-[#005EB8] rounded-xl flex items-center justify-center"><ListChecks className="w-5 h-5 text-white" /></span>
          <span className="text-left">
            <span className="block font-bold text-slate-800">All Responses</span>
            <span className="block text-xs text-slate-400">One row per person: department{teams ? ' and partner team' : ''}, never a name</span>
          </span>
        </span>
        <span className="flex items-center gap-2 text-xs text-slate-500">
          {submissions.length} responses
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <th className="text-left font-semibold px-3 py-2">Date</th>
                  <th className="text-left font-semibold px-3 py-2">Department</th>
                  {teams && <th className="text-left font-semibold px-3 py-2">Partner team</th>}
                  {showBoard && <th className="text-left font-semibold px-3 py-2">Board</th>}
                  {asked.map(q => <th key={q.id} className="text-center font-semibold px-2 py-2">{shortLabel(q, 24)}</th>)}
                  {written.map(q => <th key={q.id} className="text-left font-semibold px-3 py-2">{shortLabel(q, 32)}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map(s => (
                  <tr key={s.id} className="hover:bg-slate-50/50 align-top">
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDay(s.submittedOn)}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{placeName(round, slotOf(s)) || '-'}</td>
                    {teams && <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{partnerName(round, s.partnerId) || <span className="text-slate-300">-</span>}</td>}
                    {showBoard && <td className="px-3 py-2">{s.board ? <BoardTag /> : <span className="text-slate-300">-</span>}</td>}
                    {asked.map(q => <td key={q.id} className="px-2 py-2 text-center tabular-nums font-semibold text-slate-700">{answerText(q, s.answers[q.id])}</td>)}
                    {written.map(q => <td key={q.id} className="px-3 py-2 text-slate-600 min-w-[240px]">{answerText(q, s.answers[q.id])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
